import { GitCommit, PenBoxIcon } from 'lucide-react';

import { Icon } from '@@/Icon';
import { ResourceDetailHeader } from '@@/ResourceDetailHeader/ResourceDetailHeader';
import { HeaderStats } from '@@/ResourceDetailHeader/HeaderStats';
import { ResourceStatBlock } from '@@/ResourceDetailHeader/ResourceStatBlock';
import { ActionBarShell } from '@@/ResourceDetailHeader/ActionBarShell';
import { LinkButton } from '@@/LinkButton';

import { Workflow } from '../types';
import { StatusBadge } from '../../components/StatusBadge';
import { effectiveWorkflowStatus, computeTargetRollup } from '../status';
import { getDeployedStackLink } from '../utils';
import {
  getRealObject,
  isHelmObject,
  MixedArtifactObject,
} from '../queries/useArtifactRealObjects';

import { WorkflowDeleteButton } from './WorkflowDeleteButton';

interface Props {
  workflow: Workflow;
  artifactObjects: MixedArtifactObject[];
}

export function WorkflowResourceHeader({ workflow, artifactObjects }: Props) {
  const status = effectiveWorkflowStatus(workflow);
  const rollup = computeTargetRollup(workflow);
  const editLink = getEditLink(workflow, artifactObjects);

  return (
    <ResourceDetailHeader
      icon={<Icon icon={GitCommit} size="xl" />}
      title={workflow.name}
      badge={<StatusBadge status={status.status} />}
      rightInfo={
        <HeaderStats>
          <ResourceStatBlock
            status={rollup.tone}
            data-cy="workflow-targets-stat"
          >
            <ResourceStatBlock.Label>Targets</ResourceStatBlock.Label>
            <ResourceStatBlock.Value align="center" size="base" dot>
              {rollup.total > 0
                ? `${rollup.synced}/${rollup.total} synced`
                : 'No artifacts'}
            </ResourceStatBlock.Value>
          </ResourceStatBlock>
        </HeaderStats>
      }
      actionBar={
        <ActionBarShell>
          <div className="flex items-center gap-2">
            <LinkButton
              icon={PenBoxIcon}
              color="light"
              to={editLink?.to || ''}
              params={editLink?.params}
              disabled={!editLink}
              data-cy="workflow-edit-button"
            >
              Edit
            </LinkButton>
          </div>
          <div className="ml-auto">
            <WorkflowDeleteButton workflow={workflow} />
          </div>
        </ActionBarShell>
      }
    />
  );
}

function getEditLink(
  workflow: Workflow,
  artifactObjects: MixedArtifactObject[]
) {
  const [artifact] = workflow.artifacts;

  if (!artifact) {
    return {
      to: 'portainer.gitops.workflows.edit',
      params: { workflowId: workflow.id },
    };
  }

  const realObject = getRealObject(artifactObjects, artifact.id, artifact.type);

  if (
    workflow.artifacts.length === 1 &&
    (artifact.type === 'stack' || (!!realObject && isHelmObject(realObject)))
  ) {
    return getDeployedStackLink(artifact, artifactObjects);
  }

  return {
    to: 'portainer.gitops.workflows.edit',
    params: { workflowId: workflow.id },
  };
}
